import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Car, MapPin, Calendar, Clock, DollarSign, Users, 
  ArrowLeft, ArrowRight, Check, Phone, User, Plus, X,
  Navigation, AlertCircle, Repeat, Flag, CreditCard, Banknote, Locate
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../services/api';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom markers for pickup (green) and dropoff (red)
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const pickupIcon = createIcon('green');
const dropoffIcon = createIcon('red');
const stopIcon = createIcon('orange');

// NC ZIP code coordinates (expanded coverage)
const NC_ZIP_COORDS = {
  // Eastern NC
  '27886': { lat: 35.8968, lng: -77.5355, city: 'Tarboro' },
  '27801': { lat: 35.9382, lng: -77.7905, city: 'Rocky Mount' },
  '27893': { lat: 35.7212, lng: -77.9281, city: 'Wilson' },
  '27858': { lat: 35.6127, lng: -77.3664, city: 'Greenville' },
  '27834': { lat: 35.5959, lng: -77.4127, city: 'Greenville' },
  '27856': { lat: 35.9743, lng: -77.9652, city: 'Nashville' },
  '27809': { lat: 35.9051, lng: -77.6527, city: 'Battleboro' },
  '27863': { lat: 35.7671, lng: -77.4793, city: 'Pinetops' },
  '27828': { lat: 35.5935, lng: -77.5852, city: 'Farmville' },
  '27864': { lat: 35.8961, lng: -77.5100, city: 'Princeville' },
  '27822': { lat: 35.7951, lng: -77.8641, city: 'Elm City' },
  // Triangle Area
  '27601': { lat: 35.7796, lng: -78.6382, city: 'Raleigh' },
  '27603': { lat: 35.7355, lng: -78.6589, city: 'Raleigh' },
  '27604': { lat: 35.8197, lng: -78.5801, city: 'Raleigh' },
  '27605': { lat: 35.7934, lng: -78.6597, city: 'Raleigh' },
  '27606': { lat: 35.7571, lng: -78.7194, city: 'Raleigh' },
  '27607': { lat: 35.8075, lng: -78.7028, city: 'Raleigh' },
  '27608': { lat: 35.8177, lng: -78.6493, city: 'Raleigh' },
  '27609': { lat: 35.8414, lng: -78.6338, city: 'Raleigh' },
  '27610': { lat: 35.8315, lng: -78.5475, city: 'Raleigh' },
  '27612': { lat: 35.8487, lng: -78.7058, city: 'Raleigh' },
  '27613': { lat: 35.9125, lng: -78.7106, city: 'Raleigh' },
  '27614': { lat: 35.9359, lng: -78.6182, city: 'Raleigh' },
  '27615': { lat: 35.8977, lng: -78.6311, city: 'Raleigh' },
  '27616': { lat: 35.8616, lng: -78.5358, city: 'Raleigh' },
  '27617': { lat: 35.9037, lng: -78.7741, city: 'Raleigh' },
  '27701': { lat: 35.9940, lng: -78.8986, city: 'Durham' },
  '27703': { lat: 35.9595, lng: -78.8572, city: 'Durham' },
  '27704': { lat: 36.0340, lng: -78.8688, city: 'Durham' },
  '27705': { lat: 36.0147, lng: -78.9472, city: 'Durham' },
  '27707': { lat: 35.9544, lng: -78.9456, city: 'Durham' },
  '27708': { lat: 35.9990, lng: -78.9401, city: 'Durham' },
  '27709': { lat: 35.9240, lng: -78.8555, city: 'Durham' },
  '27712': { lat: 36.0871, lng: -78.9084, city: 'Durham' },
  '27713': { lat: 35.9047, lng: -78.9379, city: 'Durham' },
  '27514': { lat: 35.9132, lng: -79.0558, city: 'Chapel Hill' },
  '27516': { lat: 35.8393, lng: -79.1028, city: 'Chapel Hill' },
  '27517': { lat: 35.8751, lng: -79.0105, city: 'Chapel Hill' },
  '27519': { lat: 35.7996, lng: -78.8784, city: 'Cary' },
  '27511': { lat: 35.7676, lng: -78.7812, city: 'Cary' },
  '27513': { lat: 35.8007, lng: -78.8087, city: 'Cary' },
  '27518': { lat: 35.7320, lng: -78.7651, city: 'Cary' },
  '27560': { lat: 35.8493, lng: -78.8247, city: 'Morrisville' },
  '27502': { lat: 35.6307, lng: -78.8253, city: 'Apex' },
  '27523': { lat: 35.6760, lng: -78.8929, city: 'Apex' },
  '27526': { lat: 35.5707, lng: -78.6508, city: 'Fuquay-Varina' },
  '27529': { lat: 35.6301, lng: -78.5257, city: 'Garner' },
  '27540': { lat: 35.5960, lng: -78.7867, city: 'Holly Springs' },
  '27587': { lat: 35.9796, lng: -78.5055, city: 'Wake Forest' },
  '27596': { lat: 36.0188, lng: -78.4424, city: 'Youngsville' },
  // Charlotte Area
  '28202': { lat: 35.2271, lng: -80.8431, city: 'Charlotte' },
  '28203': { lat: 35.2076, lng: -80.8577, city: 'Charlotte' },
  '28204': { lat: 35.2171, lng: -80.8177, city: 'Charlotte' },
  '28205': { lat: 35.2271, lng: -80.7827, city: 'Charlotte' },
  '28206': { lat: 35.2571, lng: -80.8177, city: 'Charlotte' },
  '28207': { lat: 35.1971, lng: -80.8227, city: 'Charlotte' },
  '28208': { lat: 35.2371, lng: -80.9027, city: 'Charlotte' },
  '28209': { lat: 35.1771, lng: -80.8527, city: 'Charlotte' },
  '28210': { lat: 35.1171, lng: -80.8827, city: 'Charlotte' },
  '28211': { lat: 35.1671, lng: -80.7927, city: 'Charlotte' },
  '28212': { lat: 35.1871, lng: -80.7427, city: 'Charlotte' },
  '28213': { lat: 35.2771, lng: -80.7527, city: 'Charlotte' },
  '28214': { lat: 35.2771, lng: -80.9527, city: 'Charlotte' },
  '28215': { lat: 35.2371, lng: -80.7127, city: 'Charlotte' },
  '28216': { lat: 35.3071, lng: -80.8827, city: 'Charlotte' },
  '28217': { lat: 35.1671, lng: -80.9127, city: 'Charlotte' },
  // Fayetteville
  '28301': { lat: 35.0527, lng: -78.8784, city: 'Fayetteville' },
  '28303': { lat: 35.0768, lng: -78.9503, city: 'Fayetteville' },
  '28304': { lat: 35.0299, lng: -78.9703, city: 'Fayetteville' },
  '28305': { lat: 35.0749, lng: -78.8743, city: 'Fayetteville' },
  '28306': { lat: 34.9849, lng: -78.8743, city: 'Fayetteville' },
  '28311': { lat: 35.1349, lng: -78.9043, city: 'Fayetteville' },
  '28314': { lat: 35.0549, lng: -79.0143, city: 'Fayetteville' },
  // Wilmington
  '28401': { lat: 34.2257, lng: -77.9447, city: 'Wilmington' },
  '28403': { lat: 34.2057, lng: -77.8847, city: 'Wilmington' },
  '28405': { lat: 34.2757, lng: -77.8547, city: 'Wilmington' },
  '28409': { lat: 34.1557, lng: -77.8647, city: 'Wilmington' },
  '28412': { lat: 34.1357, lng: -77.9247, city: 'Wilmington' },
  // Greensboro/Winston-Salem
  '27401': { lat: 36.0726, lng: -79.7920, city: 'Greensboro' },
  '27403': { lat: 36.0826, lng: -79.8320, city: 'Greensboro' },
  '27405': { lat: 36.1126, lng: -79.7620, city: 'Greensboro' },
  '27406': { lat: 36.0226, lng: -79.7520, city: 'Greensboro' },
  '27407': { lat: 36.0326, lng: -79.8720, city: 'Greensboro' },
  '27408': { lat: 36.1026, lng: -79.8120, city: 'Greensboro' },
  '27101': { lat: 36.0999, lng: -80.2442, city: 'Winston-Salem' },
  '27103': { lat: 36.0699, lng: -80.3042, city: 'Winston-Salem' },
  '27104': { lat: 36.1099, lng: -80.3142, city: 'Winston-Salem' },
  '27105': { lat: 36.1399, lng: -80.2242, city: 'Winston-Salem' },
  '27106': { lat: 36.1299, lng: -80.3342, city: 'Winston-Salem' },
  '27107': { lat: 36.0299, lng: -80.1942, city: 'Winston-Salem' },
  '27127': { lat: 35.9899, lng: -80.2742, city: 'Winston-Salem' },
};

// Default center (Tarboro, NC)
const DEFAULT_CENTER = { lat: 35.8968, lng: -77.5355 };

// Vehicle types
const VEHICLE_TYPES = [
  { id: 'sedan', label: 'Car', description: 'Up to 4 passengers', icon: '🚗', multiplier: 1.0 },
  { id: 'suv', label: 'SUV', description: 'Up to 6 passengers', icon: '🚙', multiplier: 1.25 },
];

// Map controller component to fit bounds
function MapController({ pickupCoords, dropoffCoords, stops }) {
  const map = useMap();
  
  useEffect(() => {
    const allCoords = [];
    if (pickupCoords) allCoords.push([pickupCoords.lat, pickupCoords.lng]);
    if (dropoffCoords) allCoords.push([dropoffCoords.lat, dropoffCoords.lng]);
    stops.forEach(s => {
      if (s.coords) allCoords.push([s.coords.lat, s.coords.lng]);
    });
    
    if (allCoords.length >= 2) {
      map.fitBounds(allCoords, { padding: [50, 50] });
    } else if (allCoords.length === 1) {
      map.setView(allCoords[0], 13);
    }
  }, [map, pickupCoords, dropoffCoords, stops]);
  
  return null;
}

// Lookup coordinates from zip code
const getCoordsByZip = (zip) => {
  return NC_ZIP_COORDS[zip] || null;
};

const RideBookingPageV2 = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Location, 2: Map/Route, 3: Schedule, 4: Review
  const [submitting, setSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  
  // Location states
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [routeLine, setRouteLine] = useState([]);
  
  // Booking data
  const [bookingData, setBookingData] = useState({
    customer_name: user?.full_name || '',
    customer_phone: user?.phone || '',
    customer_email: user?.email || '',
    
    pickup_address: '',
    pickup_city: '',
    pickup_state: 'NC',
    pickup_zip: '',
    pickup_notes: '',
    
    dropoff_address: '',
    dropoff_city: '',
    dropoff_state: 'NC',
    dropoff_zip: '',
    
    stops: [], // Additional stops
    
    trip_type: 'one_way',
    return_pickup_time: '',
    
    scheduled_type: 'asap',
    pickup_date: '',
    pickup_time: '',
    
    vehicle_type: 'sedan',
    passenger_count: 1,
    
    special_requests: '',
    
    payment_method: 'card',
    cash_agreed: false
  });

  useEffect(() => {
    if (user) {
      setBookingData(prev => ({
        ...prev,
        customer_name: user.full_name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email
      }));
    }
  }, [user]);

  // Update coords when zip codes change
  useEffect(() => {
    if (bookingData.pickup_zip?.length === 5) {
      const coords = getCoordsByZip(bookingData.pickup_zip);
      if (coords) {
        setPickupCoords(coords);
        if (!bookingData.pickup_city) {
          handleChange('pickup_city', coords.city);
        }
      }
    }
  }, [bookingData.pickup_zip]);

  useEffect(() => {
    if (bookingData.dropoff_zip?.length === 5) {
      const coords = getCoordsByZip(bookingData.dropoff_zip);
      if (coords) {
        setDropoffCoords(coords);
        if (!bookingData.dropoff_city) {
          handleChange('dropoff_city', coords.city);
        }
      }
    }
  }, [bookingData.dropoff_zip]);

  // Create a simple route line between points
  useEffect(() => {
    if (pickupCoords && dropoffCoords) {
      const points = [[pickupCoords.lat, pickupCoords.lng]];
      bookingData.stops.forEach(s => {
        if (s.coords) points.push([s.coords.lat, s.coords.lng]);
      });
      points.push([dropoffCoords.lat, dropoffCoords.lng]);
      setRouteLine(points);
    } else {
      setRouteLine([]);
    }
  }, [pickupCoords, dropoffCoords, bookingData.stops]);

  const handleChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    if (field.includes('zip') || field === 'vehicle_type' || field === 'trip_type' || field === 'passenger_count') {
      setEstimate(null);
    }
  };

  const addStop = () => {
    if (bookingData.stops.length >= 3) {
      toast.error('Maximum 3 additional stops');
      return;
    }
    setBookingData(prev => ({
      ...prev,
      stops: [...prev.stops, { address: '', city: '', state: 'NC', zip: '', coords: null }]
    }));
  };

  const removeStop = (index) => {
    setBookingData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
    setEstimate(null);
  };

  const updateStop = (index, field, value) => {
    setBookingData(prev => {
      const newStops = [...prev.stops];
      newStops[index] = { ...newStops[index], [field]: value };
      
      // Update coords if zip changes
      if (field === 'zip' && value.length === 5) {
        const coords = getCoordsByZip(value);
        if (coords) {
          newStops[index].coords = coords;
          if (!newStops[index].city) {
            newStops[index].city = coords.city;
          }
        }
      }
      
      return { ...prev, stops: newStops };
    });
    setEstimate(null);
  };

  const calculateEstimate = async () => {
    if (!bookingData.pickup_zip || !bookingData.dropoff_zip) {
      toast.error('Please enter pickup and dropoff ZIP codes');
      return;
    }

    setEstimating(true);
    try {
      let pickupDatetime = null;
      let isSameDay = false;
      
      if (bookingData.scheduled_type === 'scheduled' && bookingData.pickup_date && bookingData.pickup_time) {
        pickupDatetime = `${bookingData.pickup_date}T${bookingData.pickup_time}:00`;
        const today = new Date().toISOString().split('T')[0];
        isSameDay = bookingData.pickup_date === today;
      } else {
        isSameDay = true;
        pickupDatetime = new Date().toISOString();
      }
      
      const response = await api.post('/rides/estimate-fare', {
        pickup_address: `${bookingData.pickup_address}, ${bookingData.pickup_city}, NC ${bookingData.pickup_zip}`.trim(),
        dropoff_address: `${bookingData.dropoff_address}, ${bookingData.dropoff_city}, NC ${bookingData.dropoff_zip}`.trim(),
        pickup_zip: bookingData.pickup_zip,
        dropoff_zip: bookingData.dropoff_zip,
        vehicle_type: bookingData.vehicle_type,
        passenger_count: bookingData.passenger_count,
        extra_stops: bookingData.stops.length,
        is_round_trip: bookingData.trip_type === 'round_trip',
        is_same_day: isSameDay,
        pickup_datetime: pickupDatetime
      });
      
      setEstimate({
        ...response.data,
        is_round_trip: bookingData.trip_type === 'round_trip'
      });
    } catch (error) {
      console.error('Estimate error:', error);
      toast.error('Could not calculate estimate');
    }
    setEstimating(false);
  };

  const handleSubmit = async () => {
    if (!estimate) {
      toast.error('Please get a fare estimate first');
      return;
    }
    
    // Validate required fields for guests
    if (!isAuthenticated) {
      if (!bookingData.customer_name?.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (!bookingData.customer_phone?.trim()) {
        toast.error('Please enter your phone number');
        return;
      }
      if (!bookingData.customer_email?.trim()) {
        toast.error('Please enter your email address');
        return;
      }
      // Basic email validation
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.customer_email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }
    
    if (bookingData.payment_method === 'cash' && !bookingData.cash_agreed) {
      toast.error('Please agree to cash payment terms');
      return;
    }

    setSubmitting(true);
    try {
      // For guests (not authenticated), require payment first
      if (!isAuthenticated) {
        // Create Stripe checkout session for payment-first flow
        const checkoutData = {
          service_id: '3009ba18-c2f1-4eb5-8583-1fd89404e3e7', // Standard Ride ID
          scheduled_date: bookingData.pickup_date || new Date().toISOString().split('T')[0],
          scheduled_time: bookingData.pickup_time || new Date().toTimeString().slice(0, 5),
          duration_hours: 1,
          guest: {
            name: bookingData.customer_name,
            email: bookingData.customer_email,
            phone: bookingData.customer_phone,
          },
          address: {
            address: bookingData.pickup_address,
            city: bookingData.pickup_city,
            state: 'NC',
            zip_code: bookingData.pickup_zip,
          },
          special_instructions: bookingData.special_requests,
          is_ride: true,
          ride_locations: {
            pickup_address: bookingData.pickup_address,
            pickup_city: bookingData.pickup_city,
            pickup_zip: bookingData.pickup_zip,
            dropoff_address: bookingData.dropoff_address,
            dropoff_city: bookingData.dropoff_city,
            dropoff_zip: bookingData.dropoff_zip,
          },
          passenger_count: bookingData.passenger_count || 1,
          vehicle_type: bookingData.vehicle_type || 'sedan',
        };
        
        try {
          const sessionRes = await api.post('/checkout/session', checkoutData);
          
          if (sessionRes.data?.checkout_url) {
            toast.info('Opening secure payment page...');
            
            // Try to open in parent window if in iframe, otherwise current window
            // This fixes Stripe not rendering in preview iframes
            try {
              if (window.top !== window.self) {
                // We're in an iframe - try to redirect the top window
                window.top.location.href = sessionRes.data.checkout_url;
              } else {
                window.location.href = sessionRes.data.checkout_url;
              }
            } catch (e) {
              // Cross-origin iframe restriction - open in new tab
              window.open(sessionRes.data.checkout_url, '_blank');
            }
            return;
          } else {
            throw new Error('Failed to create payment session');
          }
        } catch (error) {
          console.error('Payment session error:', error);
          toast.error(error.response?.data?.detail || 'Payment is required to book your ride. Please try again.');
          setSubmitting(false);
          return;
        }
      }

      // Authenticated users can proceed with regular flow
      const rideData = {
        customer_name: bookingData.customer_name,
        customer_phone: bookingData.customer_phone,
        customer_email: bookingData.customer_email,
        pickup_location: {
          address: bookingData.pickup_address,
          city: bookingData.pickup_city,
          state: 'NC',
          zip_code: bookingData.pickup_zip,
          notes: bookingData.pickup_notes
        },
        dropoff_location: {
          address: bookingData.dropoff_address,
          city: bookingData.dropoff_city,
          state: 'NC',
          zip_code: bookingData.dropoff_zip
        },
        intermediate_stops: bookingData.stops.filter(s => s.address && s.city).map((stop, idx) => ({
          location: {
            address: stop.address,
            city: stop.city,
            state: 'NC',
            zip_code: stop.zip || ''
          },
          stop_order: idx + 1,
          wait_time_minutes: 0
        })),
        ride_type: bookingData.trip_type,
        return_pickup_time: bookingData.trip_type === 'round_trip' ? bookingData.return_pickup_time : null,
        is_asap: bookingData.scheduled_type === 'asap',
        pickup_datetime: bookingData.scheduled_type === 'scheduled' 
          ? `${bookingData.pickup_date}T${bookingData.pickup_time}:00`
          : new Date().toISOString(),
        preferred_vehicle_type: bookingData.vehicle_type,
        passenger_count: bookingData.passenger_count,
        special_instructions: bookingData.special_requests,
        estimated_fare: estimate.estimated_fare,
        distance_miles: estimate.distance_miles || estimate.estimated_miles,
        estimated_duration_minutes: estimate.estimated_duration || estimate.estimated_minutes,
        payment_method: bookingData.payment_method,
        is_weekend: estimate.is_weekend,
        is_busy: estimate.is_busy
      };

      const response = await api.post('/rides/request', rideData);
      
      if (bookingData.payment_method === 'cash') {
        toast.success('Ride booked! Have cash ready at pickup.');
      } else {
        toast.success('Ride booked successfully!');
      }
      
      if (isAuthenticated) {
        navigate('/customer/dashboard?tab=rides');
      } else {
        navigate(`/ride-confirmation/${response.data.ride_id}?ref=${response.data.reference}`);
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to book ride');
    }
    setSubmitting(false);
  };

  const canProceedFromStep1 = bookingData.pickup_zip && bookingData.dropoff_zip && 
                              bookingData.pickup_address && bookingData.dropoff_address;
  
  // Step 2 just needs addresses - coordinates are optional (for map display)
  const canProceedFromStep2 = bookingData.pickup_address && bookingData.dropoff_address &&
                              bookingData.pickup_zip && bookingData.dropoff_zip;

  // Step indicators
  const steps = [
    { num: 1, label: 'Pickup & Dropoff', icon: MapPin },
    { num: 2, label: 'Route Preview', icon: Navigation },
    { num: 3, label: 'When & How', icon: Calendar },
    { num: 4, label: 'Confirm', icon: Check }
  ];

  return (
    <div className="min-h-screen service-bg-rides flex flex-col">
      {/* Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-teal-500/8 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      {/* Compact Header */}
      <div className="sticky-header py-4 px-4 relative z-50">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="w-10 h-10 rounded-xl glass-card flex items-center justify-center text-slate-400 hover:text-white transition" data-testid="back-home">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="w-12 h-12 rounded-2xl icon-gradient-rides flex items-center justify-center shadow-lg glow-rides">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-['Plus_Jakarta_Sans']">Book a Ride</h1>
            <p className="text-slate-400 text-sm">Fast, reliable rides</p>
          </div>
          <span className="ml-auto px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full border border-emerald-500/30">PRIMARY</span>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-slate-900/80 backdrop-blur-lg border-b border-white/10 py-3 px-4 sticky top-[76px] z-40">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          {steps.map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <button
                onClick={() => s.num < step && setStep(s.num)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                  step === s.num 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30' 
                    : step > s.num 
                      ? 'bg-emerald-500/20 text-emerald-400 cursor-pointer hover:bg-emerald-500/30 border border-emerald-500/30' 
                      : 'bg-slate-800/50 text-slate-500 border border-slate-700/50'
                }`}
                disabled={s.num > step}
              >
                <s.icon className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
                <span className="sm:hidden text-sm font-medium">{s.num}</span>
              </button>
              {idx < steps.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 ${step > s.num ? 'bg-[#8B5CF6]' : 'bg-white/10'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-4">
          <AnimatePresence mode="wait">
            
            {/* STEP 1: Pickup & Dropoff */}
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Contact Info (collapsible for authenticated users) */}
                {!isAuthenticated && (
                  <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#8B5CF6]" />
                      Your Info
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input 
                        value={bookingData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        placeholder="Full Name"
                        className="dark-input"
                        data-testid="customer-name"
                      />
                      <Input 
                        value={bookingData.customer_phone}
                        onChange={(e) => handleChange('customer_phone', e.target.value)}
                        placeholder="Phone"
                        className="dark-input"
                        data-testid="customer-phone"
                      />
                      <Input 
                        value={bookingData.customer_email}
                        onChange={(e) => handleChange('customer_email', e.target.value)}
                        placeholder="Email"
                        type="email"
                        className="dark-input sm:col-span-2"
                        data-testid="customer-email"
                      />
                    </div>
                  </div>
                )}

                {/* Pickup Location */}
                <div className="glass-card p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg icon-gradient-rides flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-white" />
                    </div>
                    Pickup Location
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Input 
                        value={bookingData.pickup_address}
                        onChange={(e) => handleChange('pickup_address', e.target.value)}
                        placeholder="Street Address"
                        className="dark-input"
                        data-testid="pickup-address"
                      />
                    </div>
                    <Input 
                      value={bookingData.pickup_city}
                      onChange={(e) => handleChange('pickup_city', e.target.value)}
                      placeholder="City"
                      className="dark-input"
                      data-testid="pickup-city"
                    />
                    <Input 
                      value={bookingData.pickup_zip}
                      onChange={(e) => handleChange('pickup_zip', e.target.value)}
                      placeholder="ZIP Code"
                      maxLength={5}
                      className="dark-input"
                      data-testid="pickup-zip"
                    />
                  </div>
                  {pickupCoords && (
                    <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Location found: {pickupCoords.city}
                    </p>
                  )}
                </div>

                {/* Additional Stops */}
                {bookingData.stops.map((stop, idx) => (
                  <div key={idx} className="glass-card border-amber-500/30 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-black">{idx + 1}</span>
                        </div>
                        Stop {idx + 1}
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => removeStop(idx)} className="text-red-400 hover:text-red-300 h-8 px-2">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <Input 
                          value={stop.address}
                          onChange={(e) => updateStop(idx, 'address', e.target.value)}
                          placeholder="Street Address"
                          className="bg-[#0a0a0a] border-white/10 text-white"
                        />
                      </div>
                      <Input 
                        value={stop.city}
                        onChange={(e) => updateStop(idx, 'city', e.target.value)}
                        placeholder="City"
                        className="bg-[#0a0a0a] border-white/10 text-white"
                      />
                      <Input 
                        value={stop.zip}
                        onChange={(e) => updateStop(idx, 'zip', e.target.value)}
                        placeholder="ZIP Code"
                        maxLength={5}
                        className="bg-[#0a0a0a] border-white/10 text-white"
                      />
                    </div>
                  </div>
                ))}

                {bookingData.stops.length < 3 && (
                  <Button 
                    variant="outline" 
                    onClick={addStop} 
                    className="w-full border-dashed border-white/20 text-white/60 hover:text-white hover:border-yellow-500/50 h-12"
                    data-testid="add-stop-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stop (+$5 each)
                  </Button>
                )}

                {/* Dropoff Location */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                      <Flag className="w-3 h-3 text-white" />
                    </div>
                    Dropoff Location
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Input 
                        value={bookingData.dropoff_address}
                        onChange={(e) => handleChange('dropoff_address', e.target.value)}
                        placeholder="Street Address"
                        className="bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="dropoff-address"
                      />
                    </div>
                    <Input 
                      value={bookingData.dropoff_city}
                      onChange={(e) => handleChange('dropoff_city', e.target.value)}
                      placeholder="City"
                      className="bg-[#0a0a0a] border-white/10 text-white"
                      data-testid="dropoff-city"
                    />
                    <Input 
                      value={bookingData.dropoff_zip}
                      onChange={(e) => handleChange('dropoff_zip', e.target.value)}
                      placeholder="ZIP Code"
                      maxLength={5}
                      className="bg-[#0a0a0a] border-white/10 text-white"
                      data-testid="dropoff-zip"
                    />
                  </div>
                  {dropoffCoords && (
                    <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Location found: {dropoffCoords.city}
                    </p>
                  )}
                </div>

                {/* Trip Type Toggle */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'one_way', label: 'One Way', icon: ArrowRight },
                    { id: 'round_trip', label: 'Round Trip', icon: Repeat }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => handleChange('trip_type', type.id)}
                      data-testid={`trip-${type.id}`}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                        bookingData.trip_type === type.id
                          ? 'border-[#8B5CF6] bg-[#8B5CF6]/20 text-white'
                          : 'border-white/10 text-gray-400 hover:border-white/30 bg-[#111]'
                      }`}
                    >
                      <type.icon className="w-4 h-4" />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>

                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!canProceedFromStep1}
                  className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-12 text-lg"
                  data-testid="continue-to-map"
                >
                  See Route on Map
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* STEP 2: Map & Route Preview */}
            {step === 2 && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Interactive Map */}
                <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                  <div className="h-[300px] sm:h-[400px] relative">
                    <MapContainer
                      center={[pickupCoords?.lat || DEFAULT_CENTER.lat, pickupCoords?.lng || DEFAULT_CENTER.lng]}
                      zoom={11}
                      className="h-full w-full"
                      style={{ background: '#1a1a1a' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapController pickupCoords={pickupCoords} dropoffCoords={dropoffCoords} stops={bookingData.stops} />
                      
                      {pickupCoords && (
                        <Marker position={[pickupCoords.lat, pickupCoords.lng]} icon={pickupIcon} />
                      )}
                      
                      {bookingData.stops.map((stop, idx) => (
                        stop.coords && (
                          <Marker key={idx} position={[stop.coords.lat, stop.coords.lng]} icon={stopIcon} />
                        )
                      ))}
                      
                      {dropoffCoords && (
                        <Marker position={[dropoffCoords.lat, dropoffCoords.lng]} icon={dropoffIcon} />
                      )}
                      
                      {routeLine.length >= 2 && (
                        <Polyline 
                          positions={routeLine} 
                          color="#8B5CF6" 
                          weight={4}
                          opacity={0.8}
                          dashArray="10, 10"
                        />
                      )}
                    </MapContainer>
                  </div>
                  
                  {/* Route Summary */}
                  <div className="p-4 border-t border-white/10">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                        <div className="w-0.5 h-12 bg-white/20" />
                        {bookingData.stops.map((_, idx) => (
                          <React.Fragment key={idx}>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                            <div className="w-0.5 h-12 bg-white/20" />
                          </React.Fragment>
                        ))}
                        <div className="w-3 h-3 bg-red-500 rounded-full" />
                      </div>
                      <div className="flex-1 space-y-4">
                        <div>
                          <p className="text-green-400 text-sm font-medium">Pickup</p>
                          <p className="text-white">{bookingData.pickup_address}, {bookingData.pickup_city}</p>
                        </div>
                        {bookingData.stops.map((stop, idx) => (
                          <div key={idx}>
                            <p className="text-yellow-400 text-sm font-medium">Stop {idx + 1}</p>
                            <p className="text-white">{stop.address}, {stop.city}</p>
                          </div>
                        ))}
                        <div>
                          <p className="text-red-400 text-sm font-medium">Dropoff</p>
                          <p className="text-white">{bookingData.dropoff_address}, {bookingData.dropoff_city}</p>
                        </div>
                      </div>
                    </div>
                    
                    {bookingData.trip_type === 'round_trip' && (
                      <div className="mt-3 p-2 bg-[#8B5CF6]/20 rounded-lg flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-[#A78BFA]" />
                        <span className="text-[#A78BFA] text-sm">Round Trip - Return journey included</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-white/20 text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Edit Route
                  </Button>
                  <Button 
                    onClick={() => setStep(3)} 
                    disabled={!canProceedFromStep2}
                    className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                    data-testid="continue-to-schedule"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 3: Schedule & Vehicle */}
            {step === 3 && (
              <motion.div 
                key="step3"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* When? */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#8B5CF6]" />
                    When do you need a ride?
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      { id: 'asap', label: 'Now', desc: '10-20 min' },
                      { id: 'scheduled', label: 'Later', desc: 'Pick time' }
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => handleChange('scheduled_type', type.id)}
                        data-testid={`when-${type.id}`}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          bookingData.scheduled_type === type.id
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]/20'
                            : 'border-white/10 hover:border-white/30 bg-[#0a0a0a]'
                        }`}
                      >
                        <p className={`font-semibold ${bookingData.scheduled_type === type.id ? 'text-white' : 'text-gray-300'}`}>{type.label}</p>
                        <p className="text-sm text-gray-500">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                  
                  {bookingData.scheduled_type === 'scheduled' && (
                    <div className="grid sm:grid-cols-2 gap-3 p-3 bg-[#0a0a0a] rounded-xl">
                      <div>
                        <Label className="text-white/70 text-sm">Date</Label>
                        <Input 
                          type="date"
                          value={bookingData.pickup_date}
                          onChange={(e) => handleChange('pickup_date', e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="bg-[#111] border-white/10 text-white mt-1"
                          data-testid="pickup-date"
                        />
                      </div>
                      <div>
                        <Label className="text-white/70 text-sm">Time</Label>
                        <Input 
                          type="time"
                          value={bookingData.pickup_time}
                          onChange={(e) => handleChange('pickup_time', e.target.value)}
                          className="bg-[#111] border-white/10 text-white mt-1"
                          data-testid="pickup-time"
                        />
                      </div>
                    </div>
                  )}
                  
                  {bookingData.trip_type === 'round_trip' && (
                    <div className="mt-3 p-3 bg-[#8B5CF6]/10 rounded-xl">
                      <Label className="text-[#A78BFA] text-sm">Return Pickup Time (approx)</Label>
                      <Input 
                        type="time"
                        value={bookingData.return_pickup_time}
                        onChange={(e) => handleChange('return_pickup_time', e.target.value)}
                        className="bg-[#111] border-white/10 text-white mt-1"
                      />
                    </div>
                  )}
                </div>

                {/* Vehicle */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Car className="w-5 h-5 text-[#8B5CF6]" />
                    Vehicle Type
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {VEHICLE_TYPES.map(v => (
                      <button
                        key={v.id}
                        onClick={() => handleChange('vehicle_type', v.id)}
                        data-testid={`vehicle-${v.id}`}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          bookingData.vehicle_type === v.id
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]/20'
                            : 'border-white/10 hover:border-white/30 bg-[#0a0a0a]'
                        }`}
                      >
                        <span className="text-2xl block mb-1">{v.icon}</span>
                        <p className={`font-medium text-sm ${bookingData.vehicle_type === v.id ? 'text-white' : 'text-gray-300'}`}>{v.label}</p>
                        {v.multiplier > 1 && (
                          <p className="text-xs text-[#A78BFA]">+{Math.round((v.multiplier - 1) * 100)}%</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Passengers */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#8B5CF6]" />
                    Passengers
                  </h3>
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleChange('passenger_count', Math.max(1, bookingData.passenger_count - 1))}
                      className="w-10 h-10 rounded-full border-white/20 text-white"
                    >
                      -
                    </Button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{bookingData.passenger_count}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleChange('passenger_count', Math.min(8, bookingData.passenger_count + 1))}
                      className="w-10 h-10 rounded-full border-white/20 text-white"
                    >
                      +
                    </Button>
                    <span className="text-gray-400 text-sm">+$5 each after first</span>
                  </div>
                </div>

                {/* Special Requests */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <Label className="text-white/70">Special Requests (optional)</Label>
                  <Textarea 
                    value={bookingData.special_requests}
                    onChange={(e) => handleChange('special_requests', e.target.value)}
                    placeholder="Pickup notes, accessibility needs, etc."
                    rows={2}
                    className="mt-2 bg-[#0a0a0a] border-white/10 text-white"
                  />
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1 border-white/20 text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={() => { calculateEstimate(); setStep(4); }}
                    className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                    data-testid="get-estimate"
                  >
                    Get Price
                    <DollarSign className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 4: Review & Confirm */}
            {step === 4 && (
              <motion.div 
                key="step4"
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Price Estimate */}
                <div className="bg-gradient-to-br from-[#8B5CF6]/20 to-[#6D28D9]/20 border border-[#8B5CF6]/30 rounded-xl p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#8B5CF6]" />
                    Fare Estimate
                  </h3>
                  
                  {estimating ? (
                    <div className="text-center py-6">
                      <div className="animate-spin w-8 h-8 border-2 border-[#8B5CF6] border-t-transparent rounded-full mx-auto mb-3" />
                      <p className="text-gray-400">Calculating...</p>
                    </div>
                  ) : estimate ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-gray-400">
                        <span>Distance</span>
                        <span className="text-white">{(estimate.distance_miles || estimate.estimated_miles)?.toFixed(1)} mi</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Base Fare</span>
                        <span className="text-white">${estimate.base_fare?.toFixed(2)}</span>
                      </div>
                      {estimate.vehicle_multiplier > 1 && (
                        <div className="flex justify-between text-blue-400">
                          <span>{bookingData.vehicle_type.toUpperCase()} Vehicle</span>
                          <span>×{estimate.vehicle_multiplier}</span>
                        </div>
                      )}
                      {estimate.additional_passenger_fee > 0 && (
                        <div className="flex justify-between text-cyan-400">
                          <span>Extra Passengers</span>
                          <span>+${estimate.additional_passenger_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {estimate.extra_stops_fee > 0 && (
                        <div className="flex justify-between text-yellow-400">
                          <span>Additional Stops</span>
                          <span>+${estimate.extra_stops_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {estimate.is_round_trip && (
                        <div className="flex justify-between text-[#A78BFA]">
                          <span>Round Trip</span>
                          <span>×2</span>
                        </div>
                      )}
                      <div className="border-t border-white/10 pt-3 mt-3 flex justify-between items-center">
                        <span className="text-lg font-semibold text-white">Total</span>
                        <span className="text-3xl font-bold text-[#8B5CF6]">${estimate.estimated_fare?.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={calculateEstimate} className="w-full bg-[#8B5CF6]/30 text-[#A78BFA]">
                      Calculate Fare
                    </Button>
                  )}
                </div>

                {/* Trip Summary */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3">Trip Details</h3>
                  
                  {/* Pickup & Dropoff Locations */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-lg">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-green-400 font-medium uppercase tracking-wide">Pickup</p>
                        <p className="text-white font-medium truncate">{bookingData.pickup_address || 'Not set'}</p>
                        <p className="text-sm text-gray-400">{bookingData.pickup_city}{bookingData.pickup_zip ? `, ${bookingData.pickup_zip}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-lg">
                      <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Flag className="w-4 h-4 text-red-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-red-400 font-medium uppercase tracking-wide">Dropoff</p>
                        <p className="text-white font-medium truncate">{bookingData.dropoff_address || 'Not set'}</p>
                        <p className="text-sm text-gray-400">{bookingData.dropoff_city}{bookingData.dropoff_zip ? `, ${bookingData.dropoff_zip}` : ''}</p>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle, Passengers, Time */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 bg-[#0a0a0a] rounded-lg">
                      <Car className="w-5 h-5 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-white font-medium capitalize">{bookingData.vehicle_type}</p>
                      <p className="text-xs text-gray-500">Vehicle</p>
                    </div>
                    <div className="p-3 bg-[#0a0a0a] rounded-lg">
                      <Users className="w-5 h-5 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-white font-medium">{bookingData.passenger_count}</p>
                      <p className="text-xs text-gray-500">Passengers</p>
                    </div>
                    <div className="p-3 bg-[#0a0a0a] rounded-lg">
                      <Clock className="w-5 h-5 mx-auto mb-1 text-[#8B5CF6]" />
                      <p className="text-white font-medium">{bookingData.scheduled_type === 'asap' ? 'ASAP' : bookingData.pickup_time || 'Set'}</p>
                      <p className="text-xs text-gray-500">Pickup</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method - Guests can only pay online */}
                <div className="bg-[#111] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                    Payment
                  </h3>
                  
                  {/* Guest - Direct to payment */}
                  {!isAuthenticated && (
                    <div className="p-4 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/30">
                      <div className="flex items-center gap-3 mb-2">
                        <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                        <span className="font-medium text-white">Secure Online Payment</span>
                      </div>
                      <p className="text-sm text-gray-400">
                        Click "Book Ride" below to proceed to secure checkout. Your ride will be confirmed after payment.
                      </p>
                    </div>
                  )}
                  
                  {/* Authenticated users can choose payment method */}
                  {isAuthenticated && (
                    <div className="grid grid-cols-2 gap-3">
                      {/* Pay Online - Stripe */}
                      <button
                        onClick={() => handleChange('payment_method', 'card')}
                        data-testid="pay-card"
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          bookingData.payment_method === 'card'
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]/20 text-white'
                            : 'border-white/10 text-gray-400 bg-[#0a0a0a] hover:border-white/30'
                        }`}
                      >
                        <CreditCard className="w-6 h-6" />
                        <span className="font-medium">Pay Online</span>
                        <span className="text-xs opacity-60">Stripe</span>
                      </button>
                      
                      {/* Pay with Cash */}
                      <button
                        onClick={() => handleChange('payment_method', 'cash')}
                        data-testid="pay-cash"
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          bookingData.payment_method === 'cash'
                            ? 'border-green-500 bg-green-500/20 text-white'
                            : 'border-white/10 text-gray-400 bg-[#0a0a0a] hover:border-white/30'
                        }`}
                      >
                        <Banknote className="w-6 h-6" />
                        <span className="font-medium">Pay with Cash</span>
                        <span className="text-xs opacity-60">To driver</span>
                      </button>
                    </div>
                  )}
                  
                  {bookingData.payment_method === 'cash' && (
                    <div className="mt-4 p-4 bg-amber-900/20 border border-amber-500/30 rounded-xl">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-amber-200 font-medium mb-2">Cash Payment</p>
                          <p className="text-amber-200/80 text-sm mb-3">
                            Amount due: <strong className="text-amber-400 text-lg">${estimate?.estimated_fare?.toFixed(2)}</strong>
                          </p>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bookingData.cash_agreed}
                              onChange={(e) => handleChange('cash_agreed', e.target.checked)}
                              className="w-5 h-5 rounded border-amber-500 text-amber-500 bg-[#0a0a0a] accent-amber-500"
                              data-testid="cash-agree"
                            />
                            <span className="text-sm text-amber-200">
                              I agree to pay exact cash to driver at pickup
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact */}
                <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#8B5CF6]/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-[#A78BFA]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{bookingData.customer_name || 'Your Name'}</p>
                    <p className="text-gray-400 text-sm">{bookingData.customer_phone || 'Phone'}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(3)} className="flex-1 border-white/20 text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={submitting || !estimate || (isAuthenticated && bookingData.payment_method === 'cash' && !bookingData.cash_agreed)}
                    className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-12 text-lg"
                    data-testid="confirm-ride"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        {isAuthenticated ? 'Booking...' : 'Redirecting to Payment...'}
                      </>
                    ) : (
                      <>
                        {isAuthenticated ? 'Book Ride' : 'Book Ride & Pay'}
                        <Check className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default RideBookingPageV2;
